import {
  GetRowIdParams,
  ICellRendererParams,
  RowClassParams,
  RowSelectedEvent,
  SortChangedEvent,
  SortDirection,
  GridApi,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
// styles
import "@/assets/styles/table.common.css";

import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import classNames from "classnames";
import { Button, Pagination } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useElementSize } from "@/hooks/shared/element";
import {
  BasicTableProps,
  BasicTableRef,
  DefaultPageData,
  SortColDef,
} from "@/types";

import { LoadingMask } from "@/components/shared/LoadingSection";

const DefaultColConf = {
  suppressCellFlash: true,
  comparator: () => 0, // disable default sorting
};

const DefaultLoading = () => <LoadingMask />;

function BasicTableFC<T>(props: BasicTableProps<T>, ref: Ref<BasicTableRef>) {
  const {
    loading = false,
    className,
    columns,
    page = DefaultPageData,
    rowHeight,
    getRowHeight,
    getRowId,
    enableRowDetail,
    detailRowHeight = 400,
    rowDetailRender,
    loadingTemplate = DefaultLoading,
    noDataTemplate = "<span>No Data</span>",
    enablePagination,
    onPaginationChange,
    onRowDataUpdated,
    paginationClassnames,
    showRefresh = true,
    onRefreshData,
    onSortChange,
    domLayout,
    suppressClickEdit,
    onCellEditingStarted,
    onCellEditingStopped,
    selectedRowIds,
    onRowSelected,
    selectionMode,
  } = props;

  const gridRef = useRef<AgGridReact>(null);
  const [setWrapperRef, wrapperSize] = useElementSize();

  const onTableReady = useCallback(() => {}, []);

  const onGetRowID = useCallback(
    (params: GetRowIdParams<T>) => {
      return getRowId(params.data);
    },
    [getRowId],
  );

  const onRenderRowDetail = useCallback(
    (params: ICellRendererParams<T, number>) => {
      if (enableRowDetail) {
        return rowDetailRender!(params.data);
      }
      return <div></div>;
    },
    [enableRowDetail, rowDetailRender],
  );

  // const onGetRowStyle = useCallback(
  //   (params: RowClassParams<T>): RowStyle => {
  //     // bugs to fix
  //     const api = gridRef.current?.api;
  //     if (!api) return {};

  //     const rowHeight = params.node.rowHeight ?? params.node.gridRowHeight;
  //     if (!rowHeight) return {};

  //     const lastIndex = api.getDisplayedRowCount() - 1;
  //     if (lastIndex < 0) return {};

  //     const lastNode = api.getDisplayedRowAtIndex(lastIndex);
  //     if (!lastNode) return {};

  //     // your "need scrollbar?" check
  //     const bodyHeight = wrapperSize.height - (enablePagination ? 40 : 0);
  //     const needScroll = bodyHeight < api.getDisplayedRowCount() * rowHeight;

  //     // compare by row id (works great if you set getRowId)
  //     const isLastRow = params.node.id != null && params.node.id === lastNode.id;

  //     if (isLastRow && needScroll) {
  //       return { borderBottom: "none" };
  //     }
  //     return {};
  //   },
  //   [wrapperSize.height, enablePagination]
  // );

  const onTableSortChange = useCallback(
    (event: SortChangedEvent) => {
      const columns =
        (event.api
          .getColumns()
          ?.filter((col) => col.getSortIndex() != null && col.getSort() != null)
          .sort(
            (one, two) => (one.getSortIndex() ?? 0) - (two.getSortIndex() ?? 0),
          )
          .map((col) => {
            return [(col.getColDef() as SortColDef).sortKey, col.getSort()];
          }) as [string, SortDirection][]) ?? [];
      onSortChange?.(
        columns.map((one) => one[0]),
        columns.map((one) => one[1]),
      );
    },
    [onSortChange],
  );

  const selectRowCb = useCallback(
    (event: RowSelectedEvent<T>) => {
      if (
        event.source === "rowDataChanged" ||
        event.source === "api" ||
        event.source === "apiSelectAll"
      ) {
        return;
      }
      onRowSelected?.(event.node.data);
    },
    [onRowSelected],
  );

  useEffect(() => {
    setTimeout(() => {
      gridRef.current?.api?.sizeColumnsToFit();
    }, 55);
  }, [wrapperSize]);

  useEffect(() => {
    if (!loading) {
      const count = gridRef.current?.api?.getDisplayedRowCount();
      if (count === 0) {
        gridRef.current?.api?.showNoRowsOverlay();
      } else {
        gridRef.current?.api?.hideOverlay();
      }
      if (selectedRowIds) {
        gridRef.current?.api?.forEachNode((node) => {
          node.setSelected(selectedRowIds.includes(node.data.id));
        });
      }
    }
  }, [loading, selectedRowIds]);

  useImperativeHandle(ref, () => ({
    resetRowHeights: () => {
      gridRef.current?.api?.resetRowHeights();
    },
    getAgGridApi: () => gridRef.current?.api as GridApi,
  }));

  const handleBottomBorder = useCallback(
    (params: RowClassParams) => {
      const api = gridRef.current?.api;
      if (!api) return false;

      const rowHeight =
        // prefer fixed rowHeight if you set it
        params.node.rowHeight ??
        params.api.getSizesForCurrentTheme?.()?.rowHeight;
      if (!rowHeight) return false;

      const displayedCount = api.getDisplayedRowCount();
      if (displayedCount <= 0) return false;

      const lastIndex = displayedCount - 1;
      const lastNode = api.getDisplayedRowAtIndex(lastIndex);
      if (!lastNode) return false;

      // your "container smaller than list" check => need scroll
      const bodyHeight = wrapperSize.height - (enablePagination ? 40 : 0);
      const needScroll = bodyHeight < displayedCount * rowHeight;

      // safest "is last row" check
      const isLastRow = params.node.id === lastNode.id;

      return needScroll && isLastRow;
    },
    [enablePagination, wrapperSize.height],
  );

  const rowClassRules = useMemo(
    () => ({
      "no-bottom-border": handleBottomBorder,
    }),
    [handleBottomBorder],
  );

  useEffect(() => {
    // IMPORTANT: forces AG Grid to re-evaluate rowClassRules
    gridRef.current?.api?.redrawRows();
  }, [wrapperSize.height, enablePagination, page.items.length]);

  return (
    <div
      className={classNames(
        className,
        "ag-theme-quartz flex flex-col __global-common-table",
      )}
    >
      <div ref={setWrapperRef} className="grow ">
        <AgGridReact<T>
          ref={gridRef}
          masterDetail={enableRowDetail}
          rowData={page.items}
          autoSizeStrategy={{
            type: "fitGridWidth",
          }}
          loading={loading}
          onSortChanged={onTableSortChange}
          detailRowHeight={detailRowHeight}
          // getRowStyle={onGetRowStyle}
          rowClassRules={rowClassRules}
          rowHeight={rowHeight}
          getRowHeight={getRowHeight}
          suppressCellFocus={true}
          pagination={false}
          getRowId={onGetRowID}
          enableCellChangeFlash={true}
          detailCellRenderer={onRenderRowDetail}
          onRowDataUpdated={onRowDataUpdated}
          onGridReady={onTableReady}
          columnDefs={columns}
          loadingOverlayComponent={loadingTemplate}
          overlayNoRowsTemplate={noDataTemplate}
          suppressContextMenu={true}
          enableCellTextSelection={true}
          defaultColDef={DefaultColConf}
          domLayout={domLayout}
          suppressClickEdit={suppressClickEdit}
          onCellEditingStarted={onCellEditingStarted}
          onCellEditingStopped={onCellEditingStopped}
          rowSelection={selectionMode}
          onRowSelected={selectRowCb}
        />
      </div>

      {enablePagination && (
        <div
          className={classNames(
            "flex justify-end gap-2 p-2",
            paginationClassnames,
          )}
          style={{
            borderTopColor: "#ddd",
          }}
        >
          {showRefresh && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              className="border-none bg-transparent"
              onClick={() => {
                onRefreshData?.();
              }}
            />
          )}
          <Pagination
            size="small"
            total={page.total}
            current={page.page}
            pageSize={page?.size ?? 0}
            pageSizeOptions={page.sizes}
            showTotal={showTotal}
            showSizeChanger={true}
            onChange={onPaginationChange}
          />
        </div>
      )}
    </div>
  );
}

const showTotal = (total: number) => `Total ${total}`;

const BasicTable = forwardRef(BasicTableFC) as <T>(
  props: BasicTableProps<T> & { ref?: Ref<BasicTableRef> },
) => ReturnType<typeof BasicTableFC>;

export default BasicTable;
